import { useMutation } from '@apollo/client';
import ContrastOutlinedIcon from '@mui/icons-material/ContrastOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import MenuOutlinedIcon from '@mui/icons-material/MenuOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOutlinedIcon from '@mui/icons-material/WifiOutlined';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import MemoryIcon from '@mui/icons-material/Memory';
import {
    Avatar,
    Badge,
    Box,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useColorScheme,
    useMediaQuery,
} from '@mui/material';
import {
    ButtonComponent,
    colors,
    DELETE_NOTIFICATION,
    GET_USERS,
    getInitials,
    UPDATE_SETTINGS_MUTATION,
} from '@watter/shared-components';
import { userEnums } from '@watter/shared-types';
import { getUrl } from 'aws-amplify/storage';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { GET_UNREAD_NOTIFICATION_COUNT } from '../../graphql/notifications';
import { GetInitialUserSetting } from '../../graphql/query/userSetting';
import { StyleWrapper } from '../../pages/home/home-styles';
import { NotificationOverlay } from '../../pages/notifications/notification-overlay';
import { client } from '../../service/apolloClient';
import { RootState } from '../../store';
import { hideLoader, showLoader } from '../../store/reducers/appSlice';
import { setIsRefresh } from '../../store/reducers/heaterSlice';
import {
    setAllNotificationsCleared,
    setUnreadNotificationsCount,
    showNotificationSettingPopUp,
    updateNotificationPopUpPotion,
    updateNotificationSetting,
} from '../../store/reducers/notificationsSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { setUserId, setUserImageLink, setUserName } from '../../store/reducers/userSlice';
import { getDiffInSeconds } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { DeleteNotificationPopup } from '../deleteNotificationPopUp/DeleteNotificationPopup';
import DeleteAllNotification from '../icons/delete-all-notification';
import NotificationSettingIcon from '../icons/notification-setting-bell-icon';
import { HeaderStyles, ListWrapper } from './header.style';

interface Iprops {
    openMenuClick: any;
}
const HeaderBar: React.FC<Iprops> = ({ openMenuClick }) => {
    const navigate = useNavigate();
    const userId = useSelector((state: any) => state.user.userId);
    const userName = useSelector((state: any) => state.user.userName);
    const userImageLink = useSelector((state: any) => state.user.userImageLink);
    const machineId = useSelector((state: any) => state.heater.metricsData.machine_id);
    const unreadNotificationCount = useSelector((state: any) => state.notifications.count);
    const isMetricsAPICalled = useSelector((state: any) => state.heater.is_metrics_api_called);
    const internet = useSelector((state: any) => state.heater.metricsData.internet);
    const power = useSelector((state: any) => state.heater.metricsData.power);
    const heatingStatus = useSelector((state: any) => state.heater.metricsData.heatingStatus);
    const computingStatus = useSelector((state: any) => state.heater.metricsData.computingStatus);
    const matches = useMediaQuery(theme => theme.breakpoints.down('lg'));
    const { mode, setMode } = useColorScheme();
    const [updateSettings] = useMutation(UPDATE_SETTINGS_MUTATION);
    const { t } = useTranslation();
    const location = useLocation();
    const [headerContent, setHeaderContent] = useState<any>(null);
    const [currentPath, setLocationPath] = useState<string>('');
    const [profileImageUrl, setProfileImageUrl]: any = useState(null);
    const dispatch = useDispatch();
    const { isLoggedIn } = useSelector((state: RootState) => state.user);
    const [notifyMenuOpen, setNotifyMenuOpen] = useState<HTMLButtonElement | null>(null);
    const [openDeleteModal, setDeleteAllModalOpen] = useState<boolean>(false);
    const totalNotificationsCount = useSelector((state: any) => state.notifications.totalNotificationsCount);
    const cachedData = useSelector((state: any) => state.heater.cachedData);

    const [powerStatus, setPowerStatus] = useState(false);
    const [internetStatus, setInternetStatus] = useState(false);
    const show_notifications = useSelector((state: any) => state.notifications.show_notifications);

    useEffect(() => {
        const update = () => {
            if (cachedData && Object.keys(cachedData).length !== 0 && cachedData?.[`${userId}_${machineId}`] !== null) {
                const tempPowerStatus =
                    getDiffInSeconds(cachedData?.[`${userId}_${machineId}`]?.power_sensor_timestamp) < 30 &&
                    cachedData?.[`${userId}_${machineId}`]?.power_usage > 100;
                const tempInternetStatus =
                    getDiffInSeconds(cachedData?.[`${userId}_${machineId}`]?.water_sensor_timestamp) < 30;
                setInternetStatus(tempInternetStatus);
                setPowerStatus(tempPowerStatus);
            } else {
                setInternetStatus(internet);
                setPowerStatus(power);
            }
        };
        update(); // Initial update
        const interval = setInterval(update, 30000); // Update every 1/2 minute
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [internet, cachedData, power]);

    const fetchUserDetails = React.useCallback(async () => {
        try {
            const { data } = await client.query({ query: GET_USERS });
            const userId = data?.getUser?.id;
            const name = `${data?.getUser?.first_name || ''} ${data?.getUser?.last_name || ''}`.trim();
            const userImageUrl = `${data?.getUser?.profile_image || ''}`;

            if (name) {
                dispatch(setUserName(name));
            }
            if (userImageUrl) {
                dispatch(setUserImageLink(userImageUrl));
            }

            if (userId) {
                dispatch(setUserId(userId));
            } else {
                console.warn('User ID not found in the response');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    }, [dispatch]);
    const fetchUserThemeDetails = React.useCallback(async () => {
        try {
            dispatch(showLoader());
            const { data: userSettingsData } = await client.query({ query: GetInitialUserSetting });
            let sysMode = userSettingsData?.getUserSettings?.theme;
            switch (sysMode) {
                case userEnums.Theme.auto:
                    sysMode = 'system';
                    break;
                case userEnums.Theme.dark:
                    sysMode = 'dark';
                    break;
                case userEnums.Theme.light:
                    sysMode = 'light';
                    break;
                default:
            }
            setMode(sysMode);

            dispatch(
                updateNotificationSetting({
                    show_notifications: userSettingsData?.getUserSettings?.show_notifications,
                    allow_notifications: userSettingsData?.getUserSettings?.allow_notifications,
                }),
            );
            dispatch(hideLoader());
        } catch (error) {
            console.error('Error fetching user theme data:', error);
            dispatch(hideLoader());
        }
    }, [setMode, dispatch]);

    const getUnreadNotificationCount = React.useCallback(async () => {
        try {
            const { data } = await client.query({
                query: GET_UNREAD_NOTIFICATION_COUNT,
                fetchPolicy: 'network-only',
            });
            if (data && data?.getUnreadNotificationsCount) {
                dispatch(setUnreadNotificationsCount(data?.getUnreadNotificationsCount?.count));
            }
        } catch (error) {
            console.error('Error fetching unread notification count:', error);
        }
    }, [dispatch]);

    React.useEffect(() => {
        if (isLoggedIn) {
            fetchUserDetails();
            fetchUserThemeDetails();
            getUnreadNotificationCount();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    const handleThemeChange = async (event: any, newMode: any) => {
        if (newMode !== null) {
            try {
                dispatch(showLoader());
                let updatedMode = newMode;
                switch (newMode) {
                    case 'system':
                        updatedMode = userEnums.Theme.auto;
                        break;
                    case 'dark':
                        updatedMode = userEnums.Theme.dark;
                        break;
                    case 'light':
                        updatedMode = userEnums.Theme.light;
                        break;
                    default:
                }
                const input = {
                    theme: updatedMode,
                };
                const response = await updateSettings({ variables: { input } });

                console.log('response===>', response);
                setMode(updatedMode === userEnums.Theme.dark ? 'dark' : 'light');
                dispatch(
                    openToast({
                        show: true,
                        message: response.data.updateSettings?.message,
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
                dispatch(hideLoader());
                console.warn('Theme settings updated:', response.data.updateSettings);
            } catch (err) {
                dispatch(hideLoader());
                console.error('Error updating settings:', err);
            }
        }
    };
    const miniAnalytics = (icon: any, header: string, headerName: string) => {
        return (
            <Stack direction="row" alignItems="center" spacing={{ xs: 1, lg: 2 }}>
                {icon}
                <Box>
                    <Typography variant="h6" color="info.main">
                        {header}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                        {headerName}
                    </Typography>
                </Box>
            </Stack>
        );
    };
    const fetchImageUrl = React.useCallback(() => {
        return getUrl({
            path: userImageLink, // Use path instead of key
            options: {
                expiresIn: 3600,
            },
        })
            .then(result => {
                if (result && result.url) {
                    return result.url;
                }
                console.error('No URL found in the result:', result);
                return null;
            })
            .catch(err => {
                console.error('Error fetching image:', err);
                return null;
            });
    }, [userImageLink]);
    const setUserProfileImage = React.useCallback(() => {
        if (userImageLink) {
            const loadImage = async () => {
                const url = await fetchImageUrl();
                if (url) {
                    setProfileImageUrl(url);
                }
            };
            loadImage();
        }
    }, [userImageLink, fetchImageUrl]);
    const handleRefresh = React.useCallback(() => {
        dispatch(setIsRefresh(true));
    }, [dispatch]);
    const getHeaderContext = React.useCallback(
        (path: string) => {
            switch (path) {
                case NavigationConfig.Home:
                    return (
                        <Box flexDirection="row" display="flex" gap={2}>
                            <Box>
                                <Typography variant="h4">{t('dashboard')}</Typography>
                                <Typography variant="h6" display="flex" alignItems="center" color="info">
                                    {' '}
                                    {machineId}
                                </Typography>
                            </Box>
                            <Box onClick={handleRefresh} pt={0.6} sx={{ cursor: 'pointer' }}>
                                <RefreshIcon />
                            </Box>
                        </Box>
                    );
                case NavigationConfig.ProfileSetting:
                    return (
                        <Box>
                            <Typography variant="h4">{t('profile_settings')}</Typography>
                            <Typography variant="h6" display="flex" alignItems="center" color="info">
                                {' '}
                            </Typography>
                        </Box>
                    );
                case NavigationConfig.Notifications:
                    return (
                        <Box>
                            <Typography variant="h4">{t('notifications')}</Typography>
                        </Box>
                    );
                case NavigationConfig.Contactus:
                    return (
                        <Box>
                            <Typography variant="h4">{t('support')}</Typography>
                        </Box>
                    );
                case NavigationConfig.HeaterDetails:
                    return (
                        <Box>
                            <Typography variant="h4">{t('watter_details')}</Typography>
                        </Box>
                    );
                default:
                    return null;
            }
        },
        [t, machineId, handleRefresh],
    );
    React.useEffect(() => {
        const newTitle = getHeaderContext(location.pathname);
        setHeaderContent(newTitle);
        setLocationPath(location.pathname);
        setUserProfileImage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, machineId]);
    const open = Boolean(notifyMenuOpen);
    const notifyId = open ? 'simple-popover' : undefined;
    const displayPowerOrInternetStatus = () => {
        return isMetricsAPICalled && machineId && location.pathname === NavigationConfig.Home;
    };
    const handleDeleteAllNotifications = () => {
        setDeleteAllModalOpen(true);
    };
    const handleConfirmDeleteAllNotification = async () => {
        try {
            const response = await client.mutate({
                mutation: DELETE_NOTIFICATION,
                variables: {
                    deleteNotificationId: 0, // clears all
                },
            });

            console.log('response===>', response);

            if (response.data?.deleteNotification?.success) {
                dispatch(
                    openToast({
                        show: true,
                        message: response.data?.deleteNotification?.message,
                        severity: 'success',
                        isAlertOpen: true,
                    }),
                );
                dispatch(setAllNotificationsCleared(true));
            } else {
                dispatch(
                    openToast({
                        show: true,
                        message: t('deleted_notifications_failed'),
                        severity: 'error',
                        isAlertOpen: true,
                    }),
                );
            }
        } catch (error) {
            console.error('Error deleting notification:', error);
        } finally {
            setDeleteAllModalOpen(false);
        }
    };
    const navigateToViewAllNotifications = () => {
        dispatch(setAllNotificationsCleared(false));
        navigate(NavigationConfig.Notifications);
    };
    if (typeof window !== 'undefined' && window.Cypress) {
        (window as any).testHooks = {
            fetchUserDetails,
            fetchUserThemeDetails,
            handleThemeChange,
            fetchImageUrl,
            setUserProfileImage,
            handleRefresh,
            handleDeleteAllNotifications,
            handleConfirmDeleteAllNotification,
        };
    }

    const handleSettingClick = (event: React.MouseEvent<HTMLElement>) => {
        dispatch(updateNotificationPopUpPotion(event.currentTarget));
        dispatch(showNotificationSettingPopUp());
    };

    return (
        <>
            <HeaderStyles
                px={currentPath === NavigationConfig.Home ? 2 : 0}
                py={2}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                {matches && (
                    <IconButton onClick={openMenuClick}>
                        <MenuOutlinedIcon />
                    </IconButton>
                )}
                <Box display={{ xs: 'none', sm: 'block' }}>
                    {currentPath === NavigationConfig.Home && (
                        <>
                            <Typography variant="h4">
                                {t('greetings')}, {userName}!
                            </Typography>
                            <Typography variant="h6">{t('quote1')}</Typography>
                        </>
                    )}
                </Box>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <ToggleButtonGroup value={mode} exclusive onChange={handleThemeChange} aria-label="text alignment">
                        <ToggleButton value="light" aria-label="light theme">
                            <LightModeOutlinedIcon />
                        </ToggleButton>
                        <ToggleButton value="dark" aria-label="dark theme">
                            <DarkModeOutlinedIcon />
                        </ToggleButton>
                        <ToggleButton value="system" aria-label="system theme">
                            <ContrastOutlinedIcon />
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Badge
                        className="cstBadge"
                        color="secondary"
                        overlap="circular"
                        badgeContent={unreadNotificationCount}
                        variant="dot"
                        invisible={unreadNotificationCount < 0 || !show_notifications}
                    >
                        <IconButton
                            className="cstNotify"
                            aria-describedby={notifyId}
                            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                if (location.pathname !== NavigationConfig.Notifications)
                                    if (totalNotificationsCount > 0 && show_notifications)
                                        setNotifyMenuOpen(event.currentTarget);
                                    else navigateToViewAllNotifications();
                            }}
                        >
                            <NotificationsNoneOutlinedIcon />
                        </IconButton>
                    </Badge>
                    <ListWrapper
                        open={open}
                        onClose={() => {
                            setNotifyMenuOpen(null);
                        }}
                        id={notifyId}
                        anchorEl={notifyMenuOpen}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                    >
                        <NotificationOverlay
                            onClose={() => {
                                getUnreadNotificationCount();
                                setNotifyMenuOpen(null);
                            }}
                        />
                    </ListWrapper>
                    <Avatar sizes="small" sx={{ display: { xs: 'none', sm: 'flex' } }} src={profileImageUrl}>
                        {getInitials(userName)}
                    </Avatar>
                </Stack>
            </HeaderStyles>
            <StyleWrapper>
                <Stack
                    px={currentPath === NavigationConfig.Home ? 2 : 0}
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                >
                    {headerContent}
                    {displayPowerOrInternetStatus() && (
                        <Stack direction="row" spacing={{ xs: 1, lg: 3 }} justifyContent="space-between">
                            {miniAnalytics(
                                <ElectricalServicesOutlinedIcon
                                    sx={{ color: powerStatus ? colors.lightBlue : colors.lightRed }}
                                />,
                                t('power'),
                                powerStatus ? t('on') : t('off'),
                            )}
                            {miniAnalytics(
                                <DeviceThermostatIcon
                                    sx={{ color: heatingStatus ? colors.lightBlue : colors.lightRed }}
                                />,
                                t('heating'),
                                heatingStatus ? t('on') : t('off'),
                            )}
                            {miniAnalytics(
                                <MemoryIcon sx={{ color: computingStatus ? colors.lightBlue : colors.lightRed }} />,
                                t('computing'),
                                computingStatus ? t('on') : t('off'),
                            )}
                            {miniAnalytics(
                                <WifiOutlinedIcon
                                    sx={{ color: internetStatus ? colors.lightBlue : colors.lightRed }}
                                />,
                                t('internet'),
                                internetStatus ? t('on') : t('off'),
                            )}
                        </Stack>
                    )}
                    {location.pathname === NavigationConfig.Notifications && (
                        <Box className="cstNotifyAction " display="flex" alignItems="center" gap={2}>
                            {totalNotificationsCount > 0 && (
                                <HeaderStyles>
                                    <Box className="cstBtnWrapper">
                                        <ButtonComponent
                                            size="medium"
                                            variant="contained"
                                            fullWidth
                                            buttonName={t('clear_all')}
                                            className="cstBtn"
                                            onClick={() => handleDeleteAllNotifications()}
                                        />
                                    </Box>
                                </HeaderStyles>
                            )}
                            <HeaderStyles>
                                <Box className="listItem">
                                    <IconButton
                                        className="cstNotify"
                                        aria-describedby={notifyId}
                                        data-testid="notification-setting-icon"
                                        onClick={handleSettingClick}
                                    >
                                        <NotificationSettingIcon />
                                    </IconButton>
                                </Box>
                            </HeaderStyles>
                        </Box>
                    )}

                    <DeleteNotificationPopup
                        open={openDeleteModal}
                        heading={t('clearAllNotifications')}
                        subHeading={t('removeAllNotificationsMsg')}
                        acceptHandler={handleConfirmDeleteAllNotification}
                        rejectHandler={() => setDeleteAllModalOpen(false)}
                        iconComponent={<DeleteAllNotification />}
                        acceptButtonLabel={t('clear_all')}
                        rejectButtonLabel="Cancel"
                    />
                </Stack>
            </StyleWrapper>
        </>
    );
};
export { HeaderBar };
